import { Button, Divider, Flex, Text } from '@mantine/core';
import { SquareUploader } from '@dealstack/frontend/shared/components/SquareUploader/SquareUploader';
import { KycDocumentCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/KycDocumentCard/KycDocumentCard.tsx';
import { FormTemplate } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/KYCDocuments/useKYCDocumentsController.ts';
import { UseMutationResult } from '@tanstack/react-query';
import type { KycPlacementValue } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/SignaturePlacement/kycSignaturePlacement.types.ts';
import { CustomDetailCategory } from '@dealstack/shared/lib/generated';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';

interface Props {
	title: string;
	description: string;
	templates: FormTemplate[];
	allTemplates: FormTemplate[];
	fieldArrayName: string;
	uploadFileMutation: UseMutationResult;
	invalidTypeMessage: string;
	category: CustomDetailCategory | null;
	onCategoryUploadClick: (category: CustomDetailCategory | null) => void;
	/** Absent where there are no global templates to pick from, which hides the button. */
	onUseTemplateClick?: (category: CustomDetailCategory | null) => void;
	onDeleteTemplate: (templateId: string) => void;
	onUpdateTemplate: (
		templateId: string,
		updates: {
			name?: string;
			instruction?: string;
			category?: CustomDetailCategory | null;
			requiresSignature?: boolean;
			useManualSignaturePlacement?: boolean;
			pendingPlacement?: KycPlacementValue | null;
		}
	) => Promise<boolean>;
	onArchiveTemplate?: (templateId: string, archived: boolean) => void;
	showDivider?: boolean;
	hasPermission: boolean;
	showArchived?: boolean;
}

export const TaxComplianceCategory = ({
	title,
	description,
	templates,
	allTemplates,
	fieldArrayName,
	uploadFileMutation,
	invalidTypeMessage,
	category,
	onCategoryUploadClick,
	onUseTemplateClick,
	onDeleteTemplate,
	onUpdateTemplate,
	onArchiveTemplate,
	showDivider = true,
	hasPermission = true,
	showArchived = false,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const handleUploadClick = () => {
		onCategoryUploadClick(category);
	};

	return (
		<div>
			<Flex direction="column" gap="0.5rem">
				<Text size="B5Bold" c="dark.8">
					{title}
				</Text>
				<Text c="dark.2">{description}</Text>

				<Flex gap="0.5rem" wrap="wrap" mt="0.5rem">
					{hasPermission && !showArchived && (
						<div onClick={handleUploadClick} onDragEnter={handleUploadClick}>
							<SquareUploader
								uploadFileQueryRequest={uploadFileMutation}
								accept={{ 'application/pdf': ['.pdf'] }}
								invalidTypeMessage={invalidTypeMessage}
								multiple={false}
							/>
						</div>
					)}

					{hasPermission && !showArchived && onUseTemplateClick && (
						<Button
							variant="subtle"
							onClick={() => onUseTemplateClick(category)}
							data-testid={`use-template-${category ?? 'OTHER'}`}
						>
							{tMipManage('settings.tax.use_template.button')}
						</Button>
					)}

					{templates.map((template) => {
						const originalIndex = allTemplates.findIndex((t) => t.id === template.id);

						return (
							<KycDocumentCard
								key={template.id}
								formTemplate={template}
								formTemplateFieldKey={`${fieldArrayName}.${originalIndex}`}
								removeFormTemplate={() => onDeleteTemplate(template.id)}
								onDelete={() => onDeleteTemplate(template.id)}
								onUpdateTemplate={(updates) =>
									onUpdateTemplate(template.id, updates)
								}
								onArchiveTemplate={
									onArchiveTemplate
										? (archived) => onArchiveTemplate(template.id, archived)
										: undefined
								}
								showCategorySelect={true}
								isArchiveMode={showArchived}
							/>
						);
					})}

					{showArchived && templates.length === 0 && (
						<Flex direction="column" align="center" justify="center" w="100%" py="2rem">
							<Icon variant="archivebox" size="vlg" color="gray.5" />
							<Text c="gray.6" mt="0.5rem">
								{tMipManage('settings.tax.empty_archive.tax_residency')}
							</Text>
						</Flex>
					)}
				</Flex>
			</Flex>

			{showDivider && <Divider my="1rem" color="gray.2" />}
		</div>
	);
};
